// The rank badge: the certificate's own chariot wheel, arriving a quarter at a
// time.
//
// drawCertificate.js draws one wheel, breaking the ember frame at top centre —
// a sand disc, an orangered ring, a thin ochre ring inside it, eighteen spokes,
// an orangered hub with a sand eye. Eighteen for the parvas, the akshauhinis
// and the days of the war. Nothing on the certificate explains that, and
// nothing here does either.
//
// So the badge is not a separate trophy set. It is that wheel, incomplete: six
// spokes at Yodha, ten at Ratha, fourteen at Maharatha, and at Atiratha the
// certificate's wheel exactly — eighteen spokes, both rings, the sand eye. A
// player who earns the last one has already seen it, at the top of the page
// they are about to be given.
//
// Keyed by NAME, not by threshold: ladder() resolves the last rung to each
// player's own pool size, so the number is not the same for two players and
// cannot identify a badge.
//
// Inline SVG rather than four images. No asset pipeline, one function serves
// the 24px header and the 64px reveal, and the paint is var(--sand) and
// var(--accent) rather than a copy of them, so it cannot drift from the site.
// Rasterising it would also have meant Parcel content-hashing four new files.
//
// GEOMETRY is drawWheel()'s, scaled by 18/70 into a 36x36 box, so the two stay
// recognisably the same object. Two deliberate departures, both for the 24px
// case, where a faithful scale is invisible:
//
//   - Spokes are 0.9 wide, not the faithful 0.51. At 24px, 0.51 renders as a
//     third of a pixel and vanishes into the ground.
//   - The ochre ring is 0.5 wide, not 0.26, for the same reason.
//
// SVG's y axis runs down and pdf-lib's runs up, so the spoke loop subtracts
// where drawWheel adds. The wheel is symmetric, so this is invisible — it is
// written this way to stay diffable against the original.

// With the extension, unlike the rest of the app. Parcel resolves either, but
// Node resolves only this form, and scripts/dev/render-badges.mjs loads this
// file directly to draw the contact sheet. Same reason drawCertificate.js can
// render in Node.
import { RANKS } from "./rank.js";

// Ochre is not a CSS custom property: drawCertificate.js hardcodes it, and the
// file's header says the palette comes from resets.css "plus a gilt sampled
// from the hero". Rather than add a token for one ring, this keeps the pair
// together the way the certificate does. Change one, change the other.
const OCHRE = "#8a6116";

const C = 18; // centre
const R_DISC = 18;
const R_RING = 15.9; // orangered, the outer rule
const R_INNER = 13.4; // ochre, the thin rule; also where spokes end
const R_HUB_OUT = 3.1;
const R_SPOKE_IN = 3.3;
const R_EYE = 1.03;

function spokePath(spokes) {
  let d = "";
  for (let i = 0; i < spokes; i++) {
    const a = ((90 + (i * 360) / spokes) * Math.PI) / 180;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    d +=
      `M ${(C + R_SPOKE_IN * cos).toFixed(2)} ${(C - R_SPOKE_IN * sin).toFixed(2)} ` +
      `L ${(C + R_INNER * cos).toFixed(2)} ${(C - R_INNER * sin).toFixed(2)} `;
  }
  return d.trim();
}

// Returns an SVG string, or "" below the first rung — there is no badge for a
// player who has not reached one, by design. Callers must handle "".
//
// The name comes from RANKS, never from user data, so it is safe to hand to
// innerHTML. Keep it that way.
export default function rankBadge(name, size = 24) {
  const r = RANKS.find(x => x.name === name);
  if (!r) return "";

  // By POSITION, not by threshold. The last rung's `at` is null in RANKS —
  // ladder() resolves it per player — so any comparison against it is false and
  // Atiratha would silently lose its inner rule and its eye.
  const i = RANKS.indexOf(r);
  const isFirst = i === 0;
  const isLast = i === RANKS.length - 1;

  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 36 36" ` +
    `aria-hidden="true" focusable="false" class="rank-badge-svg">` +
    `<circle cx="${C}" cy="${C}" r="${R_DISC}" fill="var(--sand)"/>` +
    `<circle cx="${C}" cy="${C}" r="${R_RING}" fill="none" ` +
    `stroke="var(--accent)" stroke-width="1.29"/>` +
    // The inner rule arrives at Ratha. Yodha is the bare wheel.
    //
    // KEEP THIS ON ONE LINE. It is 106 characters in a file wrapped at 80,
    // and re-wrapping it across a `+` silently reintroduces a production bug.
    // There is no linter and no formatter in this repo to stop you.
    //
    // Until September 2026 it was split as `...r="${R_INNER}" fill="none" ` +
    // `stroke="${OCHRE}"...`. Every interpolation in that expression is a
    // module constant, so SWC folded the concatenation to a plain string —
    // and folding it DROPPED ` fill="none" `, emitting r="13.4stroke=" into
    // the bundle. An invalid r means the circle is never drawn, so the ochre
    // rule was missing from Ratha, Maharatha and Atiratha in production, at
    // 0.5px in a 24px header, for as long as the badges have existed.
    //
    // The trigger is CONSTANT FOLDING, not the `+` on its own. The other
    // split concatenations in this function survive because their chains keep
    // a runtime interpolation — ${size}, ${spokePath(...)} — so SWC never
    // folds them. Do not "fix" those; they are fine as they are.
    //
    // render-badges.mjs cannot catch this class of bug. It imports this file
    // directly, so it draws the source and never the bundle. Only reading the
    // built output, or the page itself, will show it.
    (isFirst
      ? ""
      : `<circle cx="${C}" cy="${C}" r="${R_INNER}" fill="none" stroke="${OCHRE}" stroke-width="0.5"/>`) +
    // fill="none" is not optional. Each spoke is an open M/L subpath, and SVG
    // fills an open subpath as if closed — these are degenerate today, so the
    // default black fill happens to paint nothing, but that is luck rather than
    // intent and would stop being true the moment a spoke gains a third point.
    `<path d="${spokePath(r.spokes)}" fill="none" stroke="var(--accent)" ` +
    `stroke-width="0.9" stroke-linecap="butt"/>` +
    `<circle cx="${C}" cy="${C}" r="${R_HUB_OUT}" fill="var(--accent)"/>` +
    // The sand eye is the last thing to arrive, and it is what makes Atiratha
    // the certificate's wheel rather than a denser version of Maharatha's.
    (isLast
      ? `<circle cx="${C}" cy="${C}" r="${R_EYE}" fill="var(--sand)"/>`
      : "") +
    `</svg>`
  );
}

// Put the badge into the header, or update the one already there.
//
// This lives here rather than in showLoggedInView.js because the crossing in
// getDOMElementsForQuizPanel.js has to call it too, and that file is already
// downstream of showLoggedInView through loadQuizHref -> loadQuizRound. An
// import back the other way would close a cycle.
//
// Without it the header badge was written once at sign-in and never again, so
// a player who crossed a rung mid-session kept the old wheel, the old title and
// the old "Rank: Yodha." for the rest of the session — contradicting the reveal
// card and the profile line on the very next screen.
//
// Not aria-hidden as a whole. The text beside it is the player's NAME, so the
// word "Yodha" appears nowhere near it: hiding the badge would hide the rank
// from a screen reader entirely rather than skip a decoration. The svg is
// hidden and the visually-hidden span carries the word. `title` is for sighted
// mouse users, who otherwise cannot learn what the wheel means, and is not the
// accessible name.
export function applyRankBadge(userNameElem, name) {
  if (!userNameElem) return;

  const existing = userNameElem.querySelector(".rank-badge");

  // rankBadge returns "" for any name that is not a rung, so this both handles
  // "no rank yet" and proves `name` came from RANKS. That matters because the
  // line below interpolates it into innerHTML: this function is exported, and
  // the next caller might reach for a display name or a certificate's issuedTo,
  // both of which are free text a user typed. Validating here means they
  // cannot, rather than meaning they must not.
  const svg = rankBadge(name);
  if (!svg) {
    if (existing) existing.remove();
    return;
  }

  if (existing && existing.title === name) return;

  const badge = existing ?? document.createElement("span");
  badge.className = "rank-badge";
  badge.title = name;
  badge.innerHTML =
    svg + `<span class="visually-hidden">Rank: ${name}. </span>`;
  if (!existing) userNameElem.prepend(badge);
}
